import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { FACEBOOK_CONFIG_ID, INSTAGRAM_CONFIG_ID } from "@polifonia/env";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { useStatsigClient } from "@statsig/react-bindings";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ArrayValues } from "type-fest";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Empty } from "@/components/Empty";
import { MotifIcon } from "@/components/MotifIcon";
import { TableRowsLoader } from "@/components/TableRowsLoader";
import { Typography } from "@/components/Typography";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import { useEnableLineChannelCreation } from "@/features/line/useEnableLineChannelCreation";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { defineStyles } from "@/internal/emotion";
import { ChannelIcon } from "@/pages/channels/components/ChannelIcon";
import { ChannelSelectModal } from "@/pages/channels/components/ChannelSelectModal";
import { ChannelStatus } from "@/pages/channels/components/ChannelStatus";
import { EmailActivation } from "@/pages/channels/components/EmailActivation";
import { LineCreateModal } from "@/pages/channels/components/LineCreateModal";
import { WccsActivation } from "@/pages/channels/components/WccsActivation";
import { WccsAddLimitModal } from "@/pages/channels/components/WccsAddLimitModal";
import { useFacebookLogin } from "@/pages/channels/hooks/useFacebookLogin";
import { useWhatsappLogin } from "@/pages/channels/hooks/useWhatsappLogin";
import { FailedConnectModal } from "@/pages/channels/pages/detail/components/FailedConnectModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const MAX_WCCS_CHANNEL_COUNT = 1;

const channelOrder = [
  "line",
  "whatsapp",
  "wccs",
  "fb",
  "ig",
  "email",
] as const satisfies Array<InterludeTypes["Channel"]["type"]>;

if (!import.meta.env.PROD) {
  const anyForAssertion: any = undefined;
  // To ensure that the `typesOrder` array contains all the channel types.
  anyForAssertion as Array<InterludeTypes["Channel"]["type"]> satisfies Array<
    ArrayValues<typeof channelOrder>
  >;
}

// Statuses we treat as "connected-equivalent" for sort priority. Email's
// "healthy" / "building_reputation" replace the legacy "connected" status that
// the backend will retire on its own timeline; pinning them alongside
// "connected" keeps email channels from dropping below other connected
// channels once that removal happens.
const CONNECTED_LIKE_STATUSES = new Set<
  InterludeTypes["ChannelWithInformation"]["status"]
>(["connected", "healthy", "building_reputation"]);

const NEEDS_ATTENTION_STATUSES = new Set<
  InterludeTypes["ChannelWithInformation"]["status"]
>([
  "disconnected",
  "token-expired",
  "warning",
  "suspended",
  "restricted",
  "not_set",
]);

type ChannelStatusFilter = "all" | "connected" | "needs-attention";

/**
 * Sorts channels by:
 * 1. Connection status (connected-equivalent channels first)
 * 2. Channel type according to predefined order
 * 3. Channel name alphabetically
 *
 * @param channels - Array of channels to sort
 * @returns Sorted array of channels
 */
const sortChannels = <
  TChannel extends Pick<
    InterludeTypes["ChannelWithInformation"],
    "status" | "type" | "name"
  >,
>(
  channels: Array<TChannel>,
) => {
  return [...channels].sort((a, b) => {
    const aConnected = CONNECTED_LIKE_STATUSES.has(a.status);
    const bConnected = CONNECTED_LIKE_STATUSES.has(b.status);
    if (aConnected !== bConnected) {
      return aConnected ? -1 : 1;
    }

    const typeOrderDiff =
      channelOrder.indexOf(a.type) - channelOrder.indexOf(b.type);
    if (typeOrderDiff !== 0) {
      return typeOrderDiff;
    }

    return a.name.localeCompare(b.name);
  });
};

const styles = defineStyles({
  emptyRow: css({ height: "180px" }),
  empty: css({
    minHeight: "64px",
  }),
  ChannelEditButton: css({
    padding: 6,
    minWidth: "auto",
    fontSize: 16,
    color: theme.colors.buttonPlainDefault,
    gap: 4,
  }),
  treeContainer: css({
    position: "relative",
    marginLeft: 4,
  }),
  treeItem: css({
    position: "relative",
    paddingLeft: 16,
    // L-shaped connector (vertical + horizontal with curve)
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      width: 12,
      height: "50%",
      borderLeft: `1px solid ${theme.colors.staticFgNote}`,
      borderBottom: `1px solid ${theme.colors.staticFgNote}`,
      borderBottomLeftRadius: 6,
    },
    // Continuing vertical line below the connector
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      bottom: 0,
      borderLeft: `1px solid ${theme.colors.staticFgNote}`,
    },
    // Last item: no continuing vertical line
    "&:last-child::after": {
      display: "none",
    },
  }),
});

const ChannelEditButton: React.FC<{
  channel: InterludeTypes["ChannelWithInformation"];
}> = ({ channel }) => {
  const { t } = useTranslation();

  if (
    channel.type === "wccs" &&
    channel.channelInformation?.type === "web-streams"
  ) {
    return (
      <Link
        to="/channels/wccs/$id/web-stream-config"
        params={{ id: channel.id }}
      >
        <Button variant="plain" css={styles.ChannelEditButton}>
          <MotifIcon un-i-motif="edit" />
          {t("common.manage", "Manage")}
        </Button>
      </Link>
    );
  }

  return (
    <Link
      to="/channels/$type/$id"
      params={{
        type: channel.type,
        id: channel.type === "line" ? channel.uuid : channel.id,
      }}
    >
      <Button variant="plain" css={styles.ChannelEditButton}>
        <MotifIcon un-i-motif="edit" />
        {t("common.manage", "Manage")}
      </Button>
    </Link>
  );
};

const ChannelName: React.FC<{
  channel: InterludeTypes["ChannelWithInformation"];
}> = ({ channel }) => {
  const { t } = useTranslation();
  const isWccs = channel.type === "wccs";
  const isWebStream =
    isWccs && channel.channelInformation.type === "web-streams";

  return (
    <Stack direction="row" spacing="10px" sx={{ alignItems: "center" }}>
      <ChannelIcon type={channel.type} isWebStream={isWebStream} />
      <Stack>
        <Typography
          variant="body"
          fontWeight={400}
          color={theme.colors.staticFgTitle}
        >
          {channel.name}
        </Typography>
        {isWccs && !isWebStream ? (
          channel.channelInformation.domains.length > 0 ? (
            channel.channelInformation.domains.map((domain) => (
              <Typography
                key={domain.id}
                variant="note"
                fontWeight={500}
                color={theme.colors.staticFgNote}
              >
                {domain.url}
              </Typography>
            ))
          ) : (
            <Typography
              variant="note"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("channels.table.column.name.domains.empty")}
            </Typography>
          )
        ) : null}
        {isWebStream ? (
          <Typography
            variant="note"
            fontWeight={500}
            color={theme.colors.staticFgNote}
          >
            {t("channels.webStreams.label")}
          </Typography>
        ) : null}
        {channel.type === "email" ? (
          <Stack direction="column" gap="4px">
            <Stack direction="row" gap="4px" alignItems="center">
              <Badge variant="plain">
                {t("channels.email.label.brandDomain")}
              </Badge>
              <Typography
                variant="note"
                fontWeight={500}
                color={theme.colors.staticFgNote}
              >
                {channel.externalChannelId}
              </Typography>
            </Stack>
            <Stack direction="column">
              {channel.channelInformation.senderDomains.map((senderDomain) => (
                <Typography
                  variant="note"
                  color={theme.colors.staticFgNote}
                  css={styles.treeItem}
                  key={senderDomain.id}
                >
                  {senderDomain.domain}
                </Typography>
              ))}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

const ChannelActivation: React.FC<{
  canUseWccsChannel: boolean;
  channel: InterludeTypes["ChannelWithInformation"];
}> = ({ canUseWccsChannel, channel }) => {
  if (
    channel.type === "wccs" &&
    canUseWccsChannel &&
    channel.channelInformation.type !== "web-streams"
  ) {
    return (
      <WccsActivation
        webTrackingEnabled={channel.channelInformation.webTrackingEnabled}
        webChatModuleEnabled={channel.channelInformation.webChatModuleEnabled}
      />
    );
  }

  if (channel.type === "email") {
    return (
      <EmailActivation
        senderDomains={channel.channelInformation.senderDomains}
        senderProfiles={channel.channelInformation.senderProfiles}
      />
    );
  }

  return null;
};

const ChannelRow: React.FC<{
  canUseWccsChannel: boolean;
  channel: InterludeTypes["ChannelWithInformation"];
}> = ({ canUseWccsChannel, channel }) => {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <ChannelName channel={channel} />
      </TableCell>
      <TableCell>
        <ChannelStatus status={channel.status} type={channel.type} />
      </TableCell>
      <TableCell>
        <ChannelActivation
          canUseWccsChannel={canUseWccsChannel}
          channel={channel}
        />
      </TableCell>
      <TableCell align="right">
        <ChannelEditButton channel={channel} />
      </TableCell>
    </TableRow>
  );
};

interface ChannelsTableProps {
  canUseWccsChannel: boolean;
  channels: Array<InterludeTypes["ChannelWithInformation"]>;
  isPending: boolean;
  statusFilter: ChannelStatusFilter;
}

const ChannelsTable: React.FC<ChannelsTableProps> = function ChannelsTable({
  canUseWccsChannel,
  channels,
  isPending,
  statusFilter,
}) {
  const { t } = useTranslation();
  const emptyMessage =
    statusFilter === "needs-attention"
      ? t("channels.empty.needsAttention", "No channels need attention.")
      : statusFilter === "connected"
        ? t("channels.empty.connected", "No connected channels.")
        : t("channels.table.empty");

  return (
    <TableContainer sx={{ height: "calc(100vh - 248px)", overflowX: "auto" }}>
      <Table aria-label="channel table" stickyHeader sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow>
            <TableCell width={400}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgNote}
              >
                {t("channels.table.column.name")}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgNote}
              >
                {t("channels.table.column.status")}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgNote}
              >
                {t("channels.table.column.activation")}
              </Typography>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {isPending ? <TableRowsLoader rowsNum={5} /> : null}
          {!isPending && channels.length === 0 ? (
            <TableRow css={styles.emptyRow}>
              <TableCell colSpan={3} align="center">
                <Stack gap="12px">
                  <Empty css={styles.empty} />
                  <Typography
                    variant="body"
                    fontWeight={400}
                    color={theme.colors.staticFgNote}
                  >
                    {emptyMessage}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : null}
          {!isPending
            ? channels.map((channel) => (
                <ChannelRow
                  key={channel.id}
                  channel={channel}
                  canUseWccsChannel={canUseWccsChannel}
                />
              ))
            : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const ChannelsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const statsigClient = useStatsigClient();
  const enableLineChannelCreation = useEnableLineChannelCreation();
  const channelsQuery = interlude.channel.useChannels();
  // Orgs that create LINE through the gate must see the channel they made,
  // even when they are outside the channel migration.
  const lineChannelsQuery = interlude.line.useChannels(undefined, {
    enabled:
      statsigClient.checkGate("channel_migration") || enableLineChannelCreation,
  });
  const channels = useMemo(() => {
    const lineChannels = lineChannelsQuery.isSuccess
      ? lineChannelsQuery.data.channels
      : [];
    const otherChannels = channelsQuery.isSuccess
      ? channelsQuery.data.channels
      : [];
    return sortChannels([...lineChannels, ...otherChannels]);
  }, [
    channelsQuery.data?.channels,
    channelsQuery.isSuccess,
    lineChannelsQuery.data?.channels,
    lineChannelsQuery.isSuccess,
  ]);
  const [addChannelModalOpen, toggleAddChannelModalOpen] = useSwitch(false);
  const [addWccsChannelLimitModalOpen, toggleAddWccsChannelLimitModalOpen] =
    useSwitch(false);
  const [lineCreateModalOpen, setLineCreateModalOpen] = useState(false);
  const [preselectedChannelType, setPreselectedChannelType] = useState<
    "line" | undefined
  >(undefined);

  // `strict: false` because this page also renders as the error component of
  // the channel detail routes, where `/channels`' search schema is not active.
  const search = useSearch({ strict: false });
  const createLineRequested = search["create"] === "line";

  // CAAC sends customers to `/channels?create=line` to connect LINE. The gate
  // can turn on after the first render, once the Statsig user is bound to the
  // org, so the parameter is kept until the gate is on rather than dropped.
  const openLineCreationFromUrl = useHandler(() => {
    setPreselectedChannelType("line");
    toggleAddChannelModalOpen.on();
    const { create: _, ...rest } = search;
    navigate({ to: "/channels", search: rest, replace: true });
  });

  useEffect(
    function handleCreateLineSearchParam() {
      if (!createLineRequested || !enableLineChannelCreation) return;
      openLineCreationFromUrl();
    },
    [createLineRequested, enableLineChannelCreation, openLineCreationFromUrl],
  );

  // Opening from the button never carries a deep link's preselection, so the
  // modal falls back to its default option.
  const openAddChannelModal = useHandler(() => {
    setPreselectedChannelType(undefined);
    toggleAddChannelModalOpen.on();
  });

  const organizationSetting = interlude.organization.useOrganizationSetting();

  const [statusFilter, setStatusFilter] = useState<ChannelStatusFilter>("all");

  const visibleChannels = useMemo(() => {
    if (statusFilter === "connected") {
      return channels.filter((channel) =>
        CONNECTED_LIKE_STATUSES.has(channel.status),
      );
    }
    if (statusFilter === "needs-attention") {
      return channels.filter((channel) =>
        NEEDS_ATTENTION_STATUSES.has(channel.status),
      );
    }
    return channels;
  }, [channels, statusFilter]);

  const navigateToChannel = (
    channel: InterludeTypes["ChannelWithInformation"],
  ) => {
    navigate({
      to: "/channels/$type/$id",
      params: {
        type: channel.type,
        id: channel.type === "line" ? channel.uuid : channel.id,
      },
    });
  };

  const [channelType, setChannelType] = useState<Extract<
    InterludeTypes["Channel"]["type"],
    "whatsapp" | "fb" | "ig"
  > | null>(null);
  const [openFailedConnectModal, toggleFailedConnectModal] = useSwitch();
  const [failReason, setFailReason] = useState<string | null>(null);

  const createMutation = interlude.channel.useCreateChannel(undefined, {
    onSuccess: (channel) => {
      // A web-streams channel has no WebChat settings, so `/channels/wccs/$id`
      // is the wrong destination for it. Interlude always populates
      // `channelInformation.type` on the create response (defaulting to
      // "web-chat"), so the created channel itself — not the request — decides
      // which settings page to open.
      if (
        channel.type === "wccs" &&
        channel.channelInformation.type === "web-streams"
      ) {
        navigate({
          to: "/channels/wccs/$id/web-stream-config",
          params: { id: channel.id },
        });
        return;
      }

      navigateToChannel(channel);
    },
    onError: async (error, variables) => {
      if (
        variables.type !== "whatsapp" &&
        variables.type !== "fb" &&
        variables.type !== "ig"
      )
        return;

      const handled = await handleNonGlobalApiError(error, {
        CHANNEL_ALREADY_EXISTS() {
          setFailReason(t("resource.channel.channelAlreadyExists"));
        },
        IG_BUSINESS_ACCOUNT_NOT_FOUND() {
          setFailReason(t("resource.channel.igBusinessAccountNotFound"));
        },
      });

      if (!handled) {
        // use default error message
        setFailReason(null);
      }

      toggleFailedConnectModal.on();
      setChannelType(variables.type);
    },
  });

  const loginWhatsappMutation = useWhatsappLogin({
    onSuccess: ({ wabaId, phoneNumberId, signupAuthCode }) => {
      createMutation.mutate({
        type: "whatsapp",
        externalChannelId: phoneNumberId,
        extra: {
          wabaId,
          signupAuthCode,
        },
      });
    },
    onError: () => {
      toggleFailedConnectModal.on();
      setChannelType("whatsapp");
    },
  });

  const loginFacebookMutation = useFacebookLogin({
    onSuccess: ({ authResponse }) => {
      createMutation.mutate({
        type: "fb",
        externalChannelId: null,
        externalChannelSecret: null,
        externalChannelAccessToken: null,
        extra: {
          signupAuthCode: authResponse.code,
        },
      });
    },
  });

  const loginInstagramMutation = useFacebookLogin({
    onSuccess: ({ authResponse }) => {
      createMutation.mutate({
        type: "ig",
        externalChannelId: null,
        externalChannelSecret: null,
        externalChannelAccessToken: null,
        extra: {
          signupAuthCode: authResponse.code,
        },
      });
    },
  });

  const closeLineCreateModal = useHandler(() => {
    setLineCreateModalOpen(false);
  });

  const handleAddChannelContinue = async (
    selectedChannel: InterludeTypes["Channel"]["type"] | "web-streams",
  ) => {
    if (selectedChannel === "email") {
      toggleAddChannelModalOpen();
      // Backend reuses the org's single ChannelEmailAccount (SendGrid
      // sub-user) across multiple email channels — each new channel just
      // gets its own API key + authenticated domain. So we send the user
      // straight to a fresh onboarding form every time; existing channels
      // are still reachable through the /channels list.
      navigate({ to: "/channels/email-onboard" });
      return;
    }

    if (selectedChannel === "line") {
      toggleAddChannelModalOpen.off();
      setLineCreateModalOpen(true);
      return;
    }

    if (selectedChannel === "web-streams") {
      if (createMutation.isPending) return;
      createMutation.mutate({
        name: "Web Streams Channel",
        type: "wccs",
        wccsType: "web-streams",
        externalChannelId: null,
        externalChannelSecret: null,
        externalChannelAccessToken: null,
      });
      return;
    }

    if (selectedChannel === "wccs") {
      if (
        organizationSetting.data?.wccsAvailableWccsChannelNum ===
          MAX_WCCS_CHANNEL_COUNT &&
        channels.some((channel) => channel.type === "wccs")
      ) {
        toggleAddWccsChannelLimitModalOpen();
        toggleAddChannelModalOpen();
        return;
      }

      if (createMutation.isPending) return;
      createMutation.mutate({
        name: "Web Channel",
        type: "wccs",
        externalChannelId: null,
        externalChannelSecret: null,
        externalChannelAccessToken: null,
      });
    }

    if (selectedChannel === "whatsapp") {
      loginWhatsappMutation.startLogin();
    }

    if (selectedChannel === "fb") {
      loginFacebookMutation.startLogin({
        config_id: FACEBOOK_CONFIG_ID,
      });
    }

    if (selectedChannel === "ig") {
      loginInstagramMutation.startLogin({
        config_id: INSTAGRAM_CONFIG_ID,
      });
    }
  };

  const canUseWccsChannel = useMemo(() => {
    return Boolean(organizationSetting.data?.wccsWebChatModuleEnabled);
  }, [organizationSetting.data]);

  return (
    <PageLayout>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="12px"
      >
        <PageTitle>{t("channels.title")}</PageTitle>
        <Button onClick={openAddChannelModal}>
          {t("channels.button.create")}
        </Button>
      </Stack>
      <Stack direction="row" gap="8px">
        <Button
          aria-pressed={statusFilter === "all"}
          variant={statusFilter === "all" ? "secondary" : "plain"}
          onClick={() => setStatusFilter("all")}
        >
          {t("channels.filters.all", "All channels")}
        </Button>
        <Button
          aria-pressed={statusFilter === "needs-attention"}
          variant={statusFilter === "needs-attention" ? "secondary" : "plain"}
          onClick={() => setStatusFilter("needs-attention")}
        >
          {t("channels.filters.needsAttention", "Needs attention")}
        </Button>
        <Button
          aria-pressed={statusFilter === "connected"}
          variant={statusFilter === "connected" ? "secondary" : "plain"}
          onClick={() => setStatusFilter("connected")}
        >
          {t("channels.filters.connected", "Connected")}
        </Button>
      </Stack>
      <ChannelsTable
        canUseWccsChannel={canUseWccsChannel}
        channels={visibleChannels}
        isPending={channelsQuery.isPending}
        statusFilter={statusFilter}
      />
      <ChannelSelectModal
        loading={createMutation.isPending}
        open={addChannelModalOpen}
        onClose={toggleAddChannelModalOpen}
        onSubmit={handleAddChannelContinue}
        preselectedChannelType={preselectedChannelType}
        wccsAvailableChannelCount={
          organizationSetting.data?.wccsAvailableWccsChannelNum ?? 0
        }
        wccsWebStreamsModuleAvailable={
          organizationSetting.data?.wccsWebStreamsModuleAvailable ?? false
        }
      />
      {/* Mounted only behind the gate: its copy is not published yet, and
          every render of an unpublished key reports a missing translation. */}
      {enableLineChannelCreation ? (
        <LineCreateModal
          open={lineCreateModalOpen}
          onClose={closeLineCreateModal}
        />
      ) : null}
      <WccsAddLimitModal
        open={addWccsChannelLimitModalOpen}
        onClose={toggleAddWccsChannelLimitModalOpen}
      />
      {channelType === null ? null : (
        <FailedConnectModal
          {...(failReason ? { message: failReason } : {})}
          channelType={channelType}
          open={openFailedConnectModal}
          onClose={toggleFailedConnectModal.off}
          onConfirm={toggleFailedConnectModal.off}
        />
      )}
    </PageLayout>
  );
};
